import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import {
  PRESET_MENUS_SEARCH_DEFAULTS,
  parsePresetMenusSearch,
} from '../../hooks/usePresetMenusRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { PresetMenuDetailsSkeleton } from '../../components/PresetMenuDetails/PresetMenuDetailsSkeleton';
import { EditPresetMenuSkeleton } from '../../components/EditPresetMenu/EditPresetMenuSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { usePresetMenu } from '@hooks/presetMenus';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type PresetMenuManagement from '../../components/PresetMenuManagement/index';
import type PresetMenuDetails from '../../components/PresetMenuDetails/index';
import type EditPresetMenu from '../../components/EditPresetMenu/index';

export const presetMenusIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preset_menus',
  validateSearch: parsePresetMenusSearch,
  search: { middlewares: [stripSearchParams(PRESET_MENUS_SEARCH_DEFAULTS)] },
  component: function PresetMenusIndexRoute() {
    const { t } = useTranslation();
    const { currentUser } = useAppRouterInitial();
    useDocumentTitle(t('common.page_titles.preset_menus'));
    const [Component, setComponent] = useState<typeof PresetMenuManagement | null>(null);
    useEffect(() => {
      import('../../components/PresetMenuManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_preset_menus')} />;
    }
    return <Component currentUser={currentUser} />;
  },
});

export const presetMenuDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preset_menus/$id/details',
  component: function PresetMenuDetailsRoute() {
    const params = presetMenuDetailsRoute.useParams();
    const { t } = useTranslation();
    const { currentUser } = useAppRouterInitial();
    const idNum = Number(params.id);
    const { data: presetMenu } = usePresetMenu(idNum);
    useDocumentTitle(presetMenu?.name ?? t('common.page_titles.preset_menus'));
    const [Component, setComponent] = useState<typeof PresetMenuDetails | null>(null);
    useEffect(() => {
      import('../../components/PresetMenuDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <PresetMenuDetailsSkeleton label={t('common.loading_preset_menus')} />;
    }
    return <Component presetMenuId={idNum} currentUser={currentUser} />;
  },
});

export const presetMenuEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/preset_menus/$id/edit',
  // RBAC: edit is a write-only surface. The guard redirects read-only users
  // (no preset_menus.write) to the read-only details page before this route's
  // component runs. Client-side (replace) — no full page reload. The server
  // policy enforces this too; this is UX.
  beforeLoad: requireCapability(CAPABILITIES.PRESET_MENUS_WRITE, ({ params }) => ({
    to: '/preset_menus/$id/details',
    params: { id: params.id },
  })),
  component: function PresetMenuEditRoute() {
    const params = presetMenuEditRoute.useParams();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const { data: presetMenu } = usePresetMenu(idNum);
    useDocumentTitle(
      presetMenu ? t('common.page_titles.edit', { name: presetMenu.name }) : t('common.page_titles.preset_menus'),
    );
    const [Component, setComponent] = useState<typeof EditPresetMenu | null>(null);
    useEffect(() => {
      import('../../components/EditPresetMenu/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <EditPresetMenuSkeleton label={t('common.loading_preset_menus')} />;
    }
    return <Component presetMenuId={idNum} />;
  },
});
